import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../components/buttons/buttons";
import Input from "../../../../../components/inputs/inputs";
import { XIcon } from "@heroicons/react/outline";
import { Beat } from "../../../../../plugins/spinners.plugin";
import { getOutletFromCookies } from "../../../../../utils/Auth";
import {
  editBusinessVerificationAction,
  showMyBusinessVerification,
} from "../../../../../redux/businessVerification/businessVerificationActions";

export default function EditBusinessKYCVerification() {
  const dispatch = useDispatch();
  const hiddenDocsInput = useRef(null);
  const hiddenProofInput = useRef(null);
  const hiddenAgreementInput = useRef(null);
  const outlet = getOutletFromCookies();

  const [data, setData] = useState({});
  const [docsImages, setDocsImages] = useState([]);
  const [image64, setImage64] = useState([]);
  const [livePhotos, setLivePhotos] = useState([]);
  const [photos64, setPhotos64] = useState([]);
  const [photosUrl, setPhotosUrl] = useState([]);
  const [agreementFiles, setAgreementFiles] = useState([]);
  const [agreementUrls, setAgreementUrls] = useState([]);
  const [agreement64, setAgreement64] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const { updating, businessVerification } = useSelector(
    (state) => state.businessVerification
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(showMyBusinessVerification());
  }, [dispatch]);

  // Seed form with existing server data once loaded
  useEffect(() => {
    if (businessVerification?.document_number) {
      setData({
        document_number: businessVerification.document_number,
        url_docs: businessVerification.documents ?? [],
        url_photos: businessVerification.pictures ?? [],
        url_agreement: businessVerification.agreement ?? [],
      });
    }
    // eslint-disable-next-line
  }, [businessVerification?.document_number]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setData({ ...data, [name]: value });
  };

  const onImageChange = (e) => {
    setDocsImages([...e.target.files]);
  };

  const handleDocsClick = (event) => {
    event.preventDefault();
    hiddenDocsInput.current.click();
  };

  const handleProofClick = (event) => {
    event.preventDefault();
    hiddenProofInput.current.click();
  };

  // Remove a newly selected (local) doc image
  const removeFile = (index) => {
    const newArr = [...docsImages];
    newArr.splice(index, 1);
    setDocsImages(newArr);
  };

  // Remove an already-uploaded doc URL
  const removeUrlDoc = (index) => {
    const newArr = [...(data.url_docs ?? [])];
    newArr.splice(index, 1);
    setData({ ...data, url_docs: newArr });
  };

  // Remove an already-uploaded photo URL
  const removeUrlPhoto = (index) => {
    const newArr = [...(data.url_photos ?? [])];
    newArr.splice(index, 1);
    setData({ ...data, url_photos: newArr });
  };

  // Remove a newly selected (local) proof photo
  const removePhoto = (index) => {
    const newArr = [...livePhotos];
    newArr.splice(index, 1);
    setLivePhotos(newArr);
  };

  // Remove an already-uploaded agreement URL
  const removeUrlAgreement = (index) => {
    const newArr = [...(data.url_agreement ?? [])];
    newArr.splice(index, 1);
    setData({ ...data, url_agreement: newArr });
  };

  // Remove a newly selected (local) agreement file
  const removeAgreement = (index) => {
    const newArr = [...agreementFiles];
    newArr.splice(index, 1);
    setAgreementFiles(newArr);
  };

  const handleAgreementClick = (event) => {
    event.preventDefault();
    hiddenAgreementInput.current.click();
  };

  useEffect(() => {
    if (!docsImages.length) return;
    const newImage64 = [];
    docsImages.forEach((image) => {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onloadend = () => {
        newImage64.push(reader.result);
        if (newImage64.length === docsImages.length) setImage64([...newImage64]);
      };
    });
  }, [docsImages]);

  const uploadProofOfLife = (e) => {
    setLivePhotos([...e.target.files]);
  };

  useEffect(() => {
    if (!livePhotos.length) return;
    const newImageUrls = [];
    const newImage64 = [];
    livePhotos.forEach((image) => {
      newImageUrls.push(URL.createObjectURL(image));
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onloadend = () => {
        newImage64.push(reader.result);
        if (newImage64.length === livePhotos.length) setPhotos64([...newImage64]);
      };
    });
    setPhotosUrl(newImageUrls);
    // eslint-disable-next-line
  }, [livePhotos]);

  useEffect(() => {
    if (!agreementFiles.length) return;
    const newUrls = [];
    const new64 = [];
    agreementFiles.forEach((file) => {
      newUrls.push(URL.createObjectURL(file));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        new64.push(reader.result);
        if (new64.length === agreementFiles.length) setAgreement64([...new64]);
      };
    });
    setAgreementUrls(newUrls);
    // eslint-disable-next-line
  }, [agreementFiles]);

  const handleSubmit = async () => {
    setSubmitted(true);
    const hasDoc = (data.url_docs?.length > 0) || image64.length > 0;
    const hasPhoto = (data.url_photos?.length > 0) || photos64.length > 0;

    if (data?.document_number && hasDoc && hasPhoto) {
      const payload = {
        business_id: outlet?.business_id,
        document_number: data.document_number,
        url_docs: data.url_docs ?? [],
        url_photos: data.url_photos ?? [],
        url_agreement: data.url_agreement ?? [],
        base64_docs: image64,
        base64_photos: photos64,
        base64_agreement: agreement64,
      };
      try {
        dispatch(editBusinessVerificationAction(payload));
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  const isDisabled =
    businessVerification?.status === "approved" ||
    businessVerification?.status === "pending";

  return (
    <div>
      <form className="sm:px-10">
        <div className="sm:grid sm:grid-cols-1 gap-1">
          {/* Status badge + Business Registration Number */}
          <div className="relative">
            <div className="absolute right-0 text-sm">
              {businessVerification?.status === "approved" && (
                <span className="px-2 py-1 rounded-md text-[#00D220] bg-[#D1FED8]">
                  Approved
                </span>
              )}
              {businessVerification?.status === "pending" && (
                <span className="px-2 py-1 rounded-md text-[#FFA927] bg-[#FFE2B6]">
                  Pending
                </span>
              )}
              {businessVerification?.status === "rejected" && (
                <span className="px-2 py-1 rounded-md text-primary bg-[#ce260026]">
                  Rejected
                </span>
              )}
            </div>
            <div>
              <Input.Label
                title={"Business Registration Number"}
                value={data?.document_number || ""}
                htmlFor={"document_number"}
                name={"document_number"}
                placeholder={"Enter registration number"}
                onChange={handleChange}
                disabled={isDisabled}
              />
              {submitted && !data?.document_number && (
                <div className="text-primary text-xs">
                  Business Registration Number is required
                </div>
              )}
            </div>
          </div>

          {/* Business Registration Document */}
          <div>
            <label htmlFor="docs" className="block text-sm font-medium">
              Business Registration Document (Accepts .pdf, .doc, .docx, .txt, .rtf)
            </label>
            <div className="mt-2">
              <button
                className="w-full border border-[#E4E4F3] bg-[#FAFAFA] dark:bg-transparent p-3 text-center text-[#9CA3AF]"
                onClick={handleDocsClick}
                disabled={isDisabled}
              >
                + Upload Document
              </button>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.rtf"
                onChange={onImageChange}
                ref={hiddenDocsInput}
              />
              {submitted && !((data.url_docs?.length > 0) || image64.length > 0) && (
                <div className="text-primary text-xs">
                  Business Registration Document is required
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-4 mb-4">
              {/* Existing server docs */}
              {data.url_docs?.map((url, index) => (
                <div
                  className="flex items-center justify-between border border-[#E4E4F3] rounded px-3 py-2 bg-[#FAFAFA] dark:bg-transparent"
                  key={`url-doc-${index}`}
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{url}</span>
                  {!isDisabled && (
                    <button
                      type="button"
                      className="h-8 w-8 bg-[#FAE9E6] rounded-full flex items-center justify-center ml-2 shrink-0"
                      onClick={() => removeUrlDoc(index)}
                    >
                      <XIcon className="h-5 w-5 text-primary" />
                    </button>
                  )}
                </div>
              ))}
              {/* Newly selected local docs */}
              {docsImages.map((file, index) => (
                <div
                  className="flex items-center justify-between border border-[#E4E4F3] rounded px-3 py-2 bg-[#FAFAFA] dark:bg-transparent"
                  key={`new-doc-${index}`}
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                  <button
                    type="button"
                    className="h-8 w-8 bg-[#FAE9E6] rounded-full flex items-center justify-center ml-2 shrink-0"
                    onClick={() => removeFile(index)}
                  >
                    <XIcon className="h-5 w-5 text-primary" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Proof of Business Address */}
          <div>
            <label htmlFor="proof" className="block text-sm font-medium">
              Proof of Business Address (Accepts images, .pdf)
            </label>
            <div className="mt-2">
              <button
                className="w-full border border-[#E4E4F3] bg-[#FAFAFA] dark:bg-transparent p-3 text-center text-[#9CA3AF]"
                onClick={handleProofClick}
                disabled={isDisabled}
              >
                + Upload File
              </button>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={uploadProofOfLife}
                ref={hiddenProofInput}
              />
              {submitted && !((data.url_photos?.length > 0) || photos64.length > 0) && (
                <div className="text-primary text-xs">
                  Proof of Business Address is required
                </div>
              )}
            </div>
            <div className="mt-4">
              {/* Newly selected local photos */}
              {photosUrl.length > 0 ? (
                <div className="grid grid-cols-1 gap-8">
                  {photosUrl.map((url, index) => (
                    <figure className="w-full h-[250px] relative" key={`new-photo-${index}`}>
                      <img src={url} className="object-cover w-full h-[250px]" alt="proof" />
                      <button
                        type="button"
                        className="h-8 w-8 bg-[#FAE9E6] rounded-full absolute right-2 top-2"
                        onClick={() => removePhoto(index)}
                      >
                        <XIcon className="h-5 w-5 text-primary mx-auto" />
                      </button>
                    </figure>
                  ))}
                </div>
              ) : (
                /* Existing server photos */
                data.url_photos?.length > 0 && (
                  <div className="grid grid-cols-1 gap-8">
                    {data.url_photos.map((url, index) => (
                      <figure className="w-full h-[250px] relative" key={`url-photo-${index}`}>
                        <img src={url} className="object-cover w-full h-[250px]" alt="proof" />
                        {!isDisabled && (
                          <button
                            type="button"
                            className="h-8 w-8 bg-[#FAE9E6] rounded-full absolute right-2 top-2"
                            onClick={() => removeUrlPhoto(index)}
                          >
                            <XIcon className="h-5 w-5 text-primary mx-auto" />
                          </button>
                        )}
                      </figure>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
          {/* Vendor Agreement Form */}
          <div>
            <label className="block text-sm font-medium">
              Upload Signed Vendor Agreement Form (Accepts images, .pdf, .doc, .docx)
            </label>
            <div className="mt-2">
              <button
                className="w-full border border-[#E4E4F3] bg-[#FAFAFA] dark:bg-transparent p-3 text-center text-[#9CA3AF]"
                onClick={handleAgreementClick}
                disabled={isDisabled}
              >
                + Upload File
              </button>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => setAgreementFiles([...e.target.files])}
                ref={hiddenAgreementInput}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-4 mb-4">
              {data.url_agreement?.map((url, index) => (
                <figure className="w-full h-[120px] relative" key={`url-agreement-${index}`}>
                  <img src={url} className="object-cover w-full h-full" alt="agreement" />
                  {!isDisabled && (
                    <button
                      type="button"
                      className="h-8 w-8 bg-[#FAE9E6] rounded-full absolute right-2 top-2"
                      onClick={() => removeUrlAgreement(index)}
                    >
                      <XIcon className="h-5 w-5 text-primary mx-auto" />
                    </button>
                  )}
                </figure>
              ))}
              {agreementUrls.map((url, index) => (
                <figure className="w-full h-[120px] relative" key={`new-agreement-${index}`}>
                  <img src={url} className="object-cover w-full h-full" alt="agreement" />
                  <button
                    type="button"
                    className="h-8 w-8 bg-[#FAE9E6] rounded-full absolute right-2 top-2"
                    onClick={() => removeAgreement(index)}
                  >
                    <XIcon className="h-5 w-5 text-primary mx-auto" />
                  </button>
                </figure>
              ))}
            </div>
            <p className="text-base text-gray-500 mt-1 mb-2">
              <a
                href="https://drive.google.com/file/d/1tZm2cEtGaDaBOqsWPAqAF8NDtY1Z2rpv/view?usp=sharing"
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                Download Vendor Agreement Form
              </a>
            </p>
          </div>
        </div>

        <div className="my-10 flex flex-col items-center justify-center">
          {!isDisabled && (
            <Button.Primary
              type="button"
              disabled={updating}
              title={updating ? <Beat color="#ffffff" /> : "Update KYC"}
              className={"sm:block rounded-md mb-6 sm:mb-0 w-full sm:w-[333px]"}
              onClick={handleSubmit}
            />
          )}
        </div>
      </form>
    </div>
  );
}
